import {
  CalendarOutlined,
  CloudUploadOutlined,
  MessageOutlined
} from "@ant-design/icons"
import { Alert, Button, Segmented, Skeleton } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { budgetFormat } from "../../utils/number/budget-format.ts"
import {
  COMPLETED_PROJECT_STATUS_LABELS,
  CustomerProjectCard
} from "./components/CustomerProjectCard.tsx"
import { CalendarEventDetailModal } from "./components/CalendarEventDetailModal.tsx"
import { ContactContractorModal } from "./components/ContactContractorModal.tsx"
import { CustomerActionRow } from "./components/CustomerActionRow.tsx"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { PortalSection } from "./components/PortalSection.tsx"
import { SharedDocumentRow } from "./components/SharedDocumentRow.tsx"
import {
  asAppointmentItems,
  milestonesAsCalendarItems,
  upcomingItems
} from "./helpers/calendar.ts"
import { currentGreetingKey } from "./helpers/greeting.ts"
import { formatPortalDate } from "./helpers/format-date.ts"
import { isOutstanding } from "./helpers/invoices.ts"
import { resolveGcContact } from "./helpers/recipient.ts"
import {
  fetchAllProjectsDocuments,
  fetchAllProjectsInvoices,
  fetchAllProjectsPhotos,
  fetchPortalAlbums,
  fetchPortalCalendarEvents,
  fetchPortalDocuments,
  fetchPortalInvoices,
  fetchPortalProjectDetail,
  fetchPortalProjects
} from "./redux/portal.thunks.ts"
import type {
  PortalCalendarItem,
  PortalInvoice,
  PortalProjectListItem
} from "./types/portal.types.ts"

function ComingUpSection({
  items,
  onOpen
}: {
  items: PortalCalendarItem[]
  onOpen: (item: PortalCalendarItem) => void
}) {
  const { t } = useTranslation()
  return (
    <PortalSection
      title={t("portal.calendar.comingUp")}
      action={
        <Link to={RoutesPaths.portal.calendar} className="text-sm text-primary">
          {t("portal.home.viewAll")}
        </Link>
      }
    >
      {items.length === 0 ? (
        <PortalEmptyState title={t("portal.calendar.comingUpEmpty")} compact />
      ) : (
        <div className="flex flex-col divide-y divide-gray-50">
          {items.map((item) => {
            const isAppointment = item.kind === "appointment"
            const whenText = isAppointment
              ? item.all_day
                ? t("portal.calendar.allDay")
                : dayjs(item.start_at).format("MMM D · h:mm A")
              : `${t("portal.calendar.estimatedDate")} · ${dayjs(
                  item.end_date || item.start_date || undefined
                ).format("MMM D")}`
            return (
              <CustomerActionRow
                key={`${item.kind}-${item.id}`}
                icon={<CalendarOutlined />}
                tone="blue"
                title={item.title}
                subtitle={
                  <>
                    {item.project_name}
                    {" · "}
                    {whenText}
                    {isAppointment && item.location && <> · {item.location}</>}
                  </>
                }
                actionLabel={t("portal.calendar.viewDetails")}
                onAction={() => onOpen(item)}
              />
            )
          })}
        </div>
      )}
    </PortalSection>
  )
}

function PortalHomePage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [contactOpen, setContactOpen] = useState(false)
  const [projectsTab, setProjectsTab] = useState<"active" | "completed">(
    "active"
  )
  const [projectsTabInitialized, setProjectsTabInitialized] = useState(false)
  const [selectedCalendarItem, setSelectedCalendarItem] =
    useState<PortalCalendarItem | null>(null)

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsFetching = useAppSelector(
    (state) => state.portal.projectsFetching
  )
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const projectsError = useAppSelector((state) => state.portal.projectsError)
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const detailFetching = useAppSelector(
    (state) => state.portal.projectDetailFetching
  )
  const detailError = useAppSelector((state) => state.portal.projectDetailError)

  const invoices = useAppSelector((state) => state.portal.invoices)
  const documents = useAppSelector((state) => state.portal.documents)
  const albums = useAppSelector((state) => state.portal.albums)

  const crossProjectInvoices = useAppSelector(
    (state) => state.portal.crossProjectInvoices
  )
  const crossProjectInvoicesLoaded = useAppSelector(
    (state) => state.portal.crossProjectInvoicesLoaded
  )
  const crossProjectDocuments = useAppSelector(
    (state) => state.portal.crossProjectDocuments
  )
  const crossProjectPhotos = useAppSelector(
    (state) => state.portal.crossProjectPhotos
  )
  const calendarEvents = useAppSelector((state) => state.portal.calendarEvents)

  // Home no longer reads a Redux-held "selected project" — the sidebar's
  // project selector is gone (My projects + a project's own workspace route
  // own that job now). A customer with exactly one authorized project still
  // sees that project's own summary directly here rather than a one-card
  // "All projects" view; two or more always lands on the combined view.
  const soleProjectId = projects.length === 1 ? projects[0].id : null
  const allProjectsMode = !soleProjectId && projects.length > 1

  useEffect(() => {
    if (!soleProjectId) return
    void dispatch(fetchPortalProjectDetail(soleProjectId))
    void dispatch(fetchPortalInvoices(soleProjectId))
    void dispatch(fetchPortalDocuments(soleProjectId))
    void dispatch(fetchPortalAlbums(soleProjectId))
  }, [dispatch, soleProjectId])

  // PortalLayout already kicks these off on sign-in for a multi-project
  // customer, but a direct visit/refresh here can beat that fetch — ask
  // again rather than showing an empty "All caught up" that is really just
  // "not loaded yet".
  useEffect(() => {
    if (allProjectsMode && projectsLoaded && !crossProjectInvoicesLoaded) {
      const refs = projects.map((p) => ({ id: p.id, lead_name: p.lead_name }))
      void dispatch(fetchAllProjectsInvoices(refs))
      void dispatch(fetchAllProjectsDocuments(refs))
      void dispatch(fetchAllProjectsPhotos(refs))
    }
  }, [
    allProjectsMode,
    projectsLoaded,
    crossProjectInvoicesLoaded,
    projects,
    dispatch
  ])

  // The endpoint itself aggregates across every accessible project when
  // `project` is omitted, so All-projects mode needs no separate fan-out —
  // one fetch covers "Coming up" in both contexts.
  useEffect(() => {
    void dispatch(fetchPortalCalendarEvents({ projectId: soleProjectId }))
  }, [dispatch, soleProjectId])

  const comingUpItems = useMemo(() => {
    const appointments = asAppointmentItems(calendarEvents)
    const milestones = soleProjectId ? milestonesAsCalendarItems(detail) : []
    return upcomingItems([...appointments, ...milestones])
  }, [calendarEvents, detail, soleProjectId])

  const openProject = (projectId: string) => {
    navigate(RoutesPaths.portal.project(projectId))
  }

  const crossOutstanding = useMemo(
    () =>
      [...crossProjectInvoices]
        .filter(isOutstanding)
        .sort((a, b) => (a.due_date ?? "").localeCompare(b.due_date ?? "")),
    [crossProjectInvoices]
  )

  const crossPendingByProject = useMemo(() => {
    const map = new Map<string, number>()
    crossOutstanding.forEach((invoice) => {
      map.set(invoice.project_id, (map.get(invoice.project_id) ?? 0) + 1)
    })
    return map
  }, [crossOutstanding])

  const crossRecentDocuments = useMemo(
    () =>
      [...crossProjectDocuments]
        .sort((a, b) => b.created.localeCompare(a.created))
        .slice(0, 4),
    [crossProjectDocuments]
  )

  const crossRecentPhotos = useMemo(
    () =>
      [...crossProjectPhotos]
        .sort((a, b) => b.created.localeCompare(a.created))
        .slice(0, 6),
    [crossProjectPhotos]
  )

  const { activeProjects, completedProjects } = useMemo(() => {
    const a: PortalProjectListItem[] = []
    const c: PortalProjectListItem[] = []
    projects.forEach((project) => {
      if (
        typeof project.project_status === "string" &&
        COMPLETED_PROJECT_STATUS_LABELS.has(project.project_status)
      ) {
        c.push(project)
      } else {
        a.push(project)
      }
    })
    return { activeProjects: a, completedProjects: c }
  }, [projects])

  // Same "default to Active unless there is nothing there" rule My
  // projects uses, applied once real counts are in.
  useEffect(() => {
    if (projectsTabInitialized || !projectsLoaded) return
    setProjectsTabInitialized(true)
    if (activeProjects.length === 0 && completedProjects.length > 0)
      setProjectsTab("completed")
  }, [
    projectsTabInitialized,
    projectsLoaded,
    activeProjects.length,
    completedProjects.length
  ])

  const visibleHomeProjects =
    projectsTab === "active" ? activeProjects : completedProjects

  const outstandingInvoices = useMemo(
    () => invoices.filter(isOutstanding),
    [invoices]
  )

  const gcContact = useMemo(
    () => resolveGcContact(detail?.project_managers),
    [detail?.project_managers]
  )

  const recentDocuments = useMemo(
    () =>
      [...documents]
        .sort((a, b) => b.created.localeCompare(a.created))
        .slice(0, 4),
    [documents]
  )

  const recentPhotos = useMemo(
    () => albums.flatMap((album) => album.attachments).slice(0, 6),
    [albums]
  )

  // "Needs your attention": today, this is only outstanding invoices — file/
  // photo requests and signature/approval items have no backend record type
  // yet, so those rows simply don't exist until one does. Each invoice is
  // its own row so it can open directly.
  const attentionItems = outstandingInvoices

  const greeting = t(
    `portal.home.greeting${capitalize(currentGreetingKey())}`,
    {
      name: userInfo?.first_name
    }
  )

  const openInvoice = (invoice: { id: string }) => {
    navigate(`${RoutesPaths.portal.payments}?invoice=${invoice.id}`)
  }

  const headerActions = (
    <>
      <Button icon={<MessageOutlined />} onClick={() => setContactOpen(true)}>
        {t("portal.home.contactContractor")}
      </Button>
      <Link to={`${RoutesPaths.portal.documents}?upload=1`}>
        <Button type="primary" icon={<CloudUploadOutlined />}>
          {t("portal.upload.buttonLabel")}
        </Button>
      </Link>
    </>
  )

  if (!projectsLoaded || projectsFetching) {
    return (
      <PortalPageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (projectsError) {
    return (
      <PortalPageContainer>
        <Alert
          type="error"
          showIcon
          message={t("portal.home.loadError")}
          action={
            <Button
              size="small"
              onClick={() => dispatch(fetchPortalProjects())}
            >
              {t("portal.home.retry")}
            </Button>
          }
        />
      </PortalPageContainer>
    )
  }

  if (projects.length === 0) {
    return (
      <PortalPageContainer>
        <PortalPageHeader title={greeting} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer>
      <PortalPageHeader
        title={greeting}
        subtitle={t("portal.home.subtitleAttention")}
        actions={headerActions}
      />

      {allProjectsMode ? (
        <div className="flex flex-col gap-6">
          <PortalSection
            title={t("portal.home.needsAttention")}
            count={crossOutstanding.length}
          >
            {crossOutstanding.length === 0 ? (
              <PortalEmptyState title={t("portal.home.allCaughtUp")} compact />
            ) : (
              <div className="flex flex-col divide-y divide-gray-50">
                {crossOutstanding.map((invoice) => (
                  <CustomerActionRow
                    key={invoice.id}
                    icon={<span className="text-base font-semibold">$</span>}
                    title={t("portal.payments.invoiceNumber", {
                      number: invoice.invoice_number
                    })}
                    subtitle={
                      <>
                        {invoice.project_name}
                        {" · "}
                        {budgetFormat(
                          Number(invoice.amount) -
                            Number(invoice.paid_to_date || 0)
                        )}
                        {invoice.due_date && (
                          <>
                            {" "}
                            · {t("portal.payments.due")}{" "}
                            {formatPortalDate(invoice.due_date)}
                          </>
                        )}
                      </>
                    }
                    actionLabel={t("portal.home.viewInvoice")}
                    onAction={() => openInvoice(invoice)}
                  />
                ))}
              </div>
            )}
          </PortalSection>

          <PortalSection
            title={t("portal.home.myProjects")}
            variant="plain"
            action={
              <Segmented
                value={projectsTab}
                onChange={(value) =>
                  setProjectsTab(value as "active" | "completed")
                }
                options={[
                  {
                    label: t("portal.projects.tabActive", {
                      count: activeProjects.length
                    }),
                    value: "active"
                  },
                  {
                    label: t("portal.projects.tabCompleted", {
                      count: completedProjects.length
                    }),
                    value: "completed"
                  }
                ]}
              />
            }
          >
            {visibleHomeProjects.length === 0 ? (
              <PortalEmptyState
                title={
                  projectsTab === "active"
                    ? t("portal.projects.emptyActive")
                    : t("portal.projects.emptyCompleted")
                }
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {visibleHomeProjects.map((project) => (
                  <CustomerProjectCard
                    key={project.id}
                    project={project}
                    pendingCount={crossPendingByProject.get(project.id) ?? 0}
                    onOpen={() => openProject(project.id)}
                  />
                ))}
              </div>
            )}
          </PortalSection>

          <ComingUpSection
            items={comingUpItems}
            onOpen={setSelectedCalendarItem}
          />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[3fr_2fr]">
            <PortalSection
              title={t("portal.home.recentPhotos")}
              action={
                <Link
                  to={RoutesPaths.portal.photos}
                  className="text-sm text-primary"
                >
                  {t("portal.home.viewAll")}
                </Link>
              }
            >
              {crossRecentPhotos.length === 0 ? (
                <PortalEmptyState
                  title={t("portal.home.recentPhotosEmpty")}
                  compact
                />
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {crossRecentPhotos.map((photo) => (
                    <Link
                      key={photo.id}
                      to={RoutesPaths.portal.photos}
                      className="aspect-square overflow-hidden rounded-md bg-gray-100"
                    >
                      <img
                        src={photo.file}
                        alt={photo.original_filename}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </PortalSection>

            <PortalSection
              title={t("portal.home.recentDocuments")}
              action={
                <Link
                  to={RoutesPaths.portal.documents}
                  className="text-sm text-primary"
                >
                  {t("portal.home.viewAll")}
                </Link>
              }
            >
              {crossRecentDocuments.length === 0 ? (
                <PortalEmptyState
                  title={t("portal.home.noDocumentsYet")}
                  compact
                />
              ) : (
                <div className="flex flex-col divide-y divide-gray-50">
                  {crossRecentDocuments.map((document) => (
                    <SharedDocumentRow
                      key={document.id}
                      fileName={document.file_name}
                      projectName={document.project_name}
                      sharedByLabel={
                        document.uploaded_by_id === userInfo?.id
                          ? t("portal.documents.sourceYou")
                          : (document.uploaded_by_name ??
                            document.uploaded_by ??
                            "—")
                      }
                      href={document.file}
                      compact
                    />
                  ))}
                </div>
              )}
            </PortalSection>
          </div>

          <ContactContractorModal
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            projectOptions={projects.map((project) => ({
              id: project.id,
              name: project.lead_name,
              contact: resolveGcContact(project.project_managers) ?? null
            }))}
          />
        </div>
      ) : (
        <>
          {detailError && (
            <Alert
              className="mb-4"
              type="error"
              showIcon
              message={t("portal.home.projectLoadError")}
            />
          )}

          {detailFetching && !detail && (
            <CardComponent>
              <Skeleton active paragraph={{ rows: 4 }} />
            </CardComponent>
          )}

          {detail && (
            <div className="flex flex-col gap-6">
              <PortalSection
                title={t("portal.home.needsAttention")}
                count={attentionItems.length}
              >
                {attentionItems.length === 0 ? (
                  <PortalEmptyState
                    title={t("portal.home.allCaughtUp")}
                    compact
                  />
                ) : (
                  <div className="flex flex-col divide-y divide-gray-50">
                    {attentionItems.map((invoice: PortalInvoice) => (
                      <CustomerActionRow
                        key={invoice.id}
                        icon={
                          <span className="text-base font-semibold">$</span>
                        }
                        title={t("portal.payments.invoiceNumber", {
                          number: invoice.invoice_number
                        })}
                        subtitle={
                          <>
                            {budgetFormat(
                              Number(invoice.amount) -
                                Number(invoice.paid_to_date || 0)
                            )}
                            {invoice.due_date && (
                              <>
                                {" "}
                                · {t("portal.payments.due")}{" "}
                                {formatPortalDate(invoice.due_date)}
                              </>
                            )}
                          </>
                        }
                        actionLabel={t("portal.home.viewInvoice")}
                        onAction={() =>
                          navigate(
                            `${RoutesPaths.portal.payments}?invoice=${invoice.id}`
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </PortalSection>

              {/* A compact summary, not this project's own Overview page
                  again — "current work", not the full milestone/document/
                  photo breakdown Overview already owns. `projects[0]`
                  (the list row), not `detail` — CustomerProjectCard reads
                  `project_status` as the string label the list endpoint
                  sends; the detail endpoint sends the same fact as a plain
                  numeric code with the label in `project_status_name`
                  instead, which silently defeats the card's own
                  isCompleted check (no Tag, wrong button copy) if passed
                  here directly. */}
              <CustomerProjectCard
                project={projects[0]}
                pendingCount={attentionItems.length}
                onOpen={() => openProject(detail.id)}
              />

              <ComingUpSection
                items={comingUpItems}
                onOpen={setSelectedCalendarItem}
              />

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-[3fr_2fr]">
                <PortalSection
                  title={t("portal.home.recentPhotos")}
                  action={
                    <Link
                      to={RoutesPaths.portal.photos}
                      className="text-sm text-primary"
                    >
                      {t("portal.home.viewAll")}
                    </Link>
                  }
                >
                  {recentPhotos.length === 0 ? (
                    <PortalEmptyState
                      title={t("portal.home.recentPhotosEmpty")}
                      compact
                    />
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                      {recentPhotos.map((photo) => (
                        <Link
                          key={photo.id}
                          to={RoutesPaths.portal.photos}
                          className="aspect-square overflow-hidden rounded-md bg-gray-100"
                        >
                          <img
                            src={photo.file}
                            alt={photo.original_filename}
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </PortalSection>

                <PortalSection
                  title={t("portal.home.recentDocuments")}
                  action={
                    <Link
                      to={RoutesPaths.portal.documents}
                      className="text-sm text-primary"
                    >
                      {t("portal.home.viewAll")}
                    </Link>
                  }
                >
                  {recentDocuments.length === 0 ? (
                    <PortalEmptyState
                      title={t("portal.home.noDocumentsYet")}
                      compact
                    />
                  ) : (
                    <div className="flex flex-col divide-y divide-gray-50">
                      {recentDocuments.map((document) => (
                        <SharedDocumentRow
                          key={document.id}
                          fileName={document.file_name}
                          sharedByLabel={
                            document.uploaded_by_id === userInfo?.id
                              ? t("portal.documents.sourceYou")
                              : (document.uploaded_by_name ??
                                document.uploaded_by ??
                                "—")
                          }
                          href={document.file}
                          compact
                        />
                      ))}
                    </div>
                  )}
                </PortalSection>
              </div>
            </div>
          )}

          <ContactContractorModal
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            project={
              soleProjectId
                ? {
                    id: soleProjectId,
                    name: detail?.lead_name ?? "",
                    contact: gcContact ?? null
                  }
                : undefined
            }
          />
        </>
      )}

      <CalendarEventDetailModal
        open={!!selectedCalendarItem}
        onClose={() => setSelectedCalendarItem(null)}
        item={selectedCalendarItem}
      />
    </PortalPageContainer>
  )
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1)

export default PortalHomePage
